import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";

/**
 * Minimal in-app confirm modal matching EQUIA's quiet-luxury aesthetic.
 * Props:
 *   open: boolean
 *   title: string
 *   description: string | ReactNode
 *   confirmLabel: string (default "Delete")
 *   cancelLabel: string (default "Cancel")
 *   tone: "danger" | "neutral"
 *   onConfirm: () => void
 *   onCancel: () => void
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  tone = "danger",
  onConfirm,
  onCancel,
}) {
  const isDanger = tone === "danger";
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          data-testid="confirm-dialog"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onCancel}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-md bg-[#0A0A0A] border border-white/[0.12] shadow-2xl"
          >
            <div className="flex items-start gap-4 p-6">
              <div
                className={`w-10 h-10 flex items-center justify-center flex-shrink-0 border ${
                  isDanger
                    ? "bg-red-500/5 border-red-500/20"
                    : "bg-[#D4AF37]/5 border-[#D4AF37]/20"
                }`}
              >
                <AlertTriangle
                  className={`w-4 h-4 ${isDanger ? "text-red-400" : "text-[#D4AF37]"}`}
                  strokeWidth={1.5}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.18em] uppercase text-white/65 mb-1.5">
                  Confirm
                </p>
                <h3
                  className="font-serif text-xl text-white tracking-tight leading-snug"
                  data-testid="confirm-dialog-title"
                >
                  {title}
                </h3>
                {description && (
                  <p
                    className="text-white/65 text-sm mt-2 leading-relaxed"
                    data-testid="confirm-dialog-description"
                  >
                    {description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/[0.10] bg-black/30">
              <button
                onClick={onCancel}
                data-testid="confirm-dialog-cancel"
                className="px-4 py-2 text-xs tracking-[0.1em] uppercase text-white/85 border border-white/10 hover:border-white/25 hover:text-white transition-colors"
              >
                {cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                data-testid="confirm-dialog-confirm"
                className={`px-4 py-2 text-xs tracking-[0.1em] uppercase font-medium transition-colors ${
                  isDanger
                    ? "bg-red-500/90 hover:bg-red-500 text-white"
                    : "bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                }`}
              >
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


/* ──────────────────────────────────────────────────────────────────────────
 * Imperative confirm API — drop-in replacement for `window.confirm()`.
 *
 * Why:  The browser's native confirm() leaks the preview URL ("getequia.com
 * says:") and ignores our dark/gold "Quiet Luxury" theme. This hook wraps
 * the controlled <ConfirmDialog> above so any code can do:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete property",
 *     body:  "This cannot be undone.",
 *     tone:  "danger",
 *   });
 *   if (!ok) return;
 *
 * Mount <ConfirmProvider> once near the app root (App.js) so the hook is
 * available in any descendant.
 * ────────────────────────────────────────────────────────────────────── */

const ConfirmCtx = createContext(null);

/** Hook returning an async confirm function. Falls back to window.confirm
 *  outside of a <ConfirmProvider> so unit-test contexts still work. */
export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    return ({ title, body }) =>
      Promise.resolve(window.confirm(`${title}\n\n${body || ""}`));
  }
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  // Hold the resolver so we can settle the promise on confirm / cancel.
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || "Are you sure?",
      body: opts.body || "",
      confirmText: opts.confirmText || "Confirm",
      cancelText: opts.cancelText || "Cancel",
      tone: opts.tone || "default",
    });
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const settle = useCallback((value) => {
    if (resolverRef.current) resolverRef.current(value);
    resolverRef.current = null;
    setState(null);
  }, []);

  // Escape cancels; Enter confirms (matches native browser dialog UX).
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") settle(false);
      if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, settle]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={!!state}
        title={state?.title}
        description={state?.body}
        confirmLabel={state?.confirmText}
        cancelLabel={state?.cancelText}
        tone={state?.tone === "danger" ? "danger" : "neutral"}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </ConfirmCtx.Provider>
  );
}
